import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { User } from 'app/models';
import { ActivityApi } from 'app/modules/dashboard/api/activity.api';
import { BehaviorSubject, Subscription } from 'rxjs';
import { GenderFilterBlockComponent } from './filterComponent/genderFilterComponent/genderFilterBlock.component';
import {
  AgeFilterBlockComponent,
  age,
} from './filterComponent/ageFilterComponent/ageFilterBlock.component';
import { EquipmentFilterBlockComponent } from './filterComponent/equipmentFilterComponent/equipmentFilterBlock.component';
import { HttpParams } from '@angular/common/http';
import { EquipmentApi } from 'app/modules/dashboard/api/equipment.api';
import { ChronoPipe } from 'app/modules/dashboard/pipes/chrono.pipe';
import { SkeletonModule } from 'primeng/skeleton';
import { PositionItemComponent } from '../ranking/positionItem/positionItem.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { UserPositionComponent } from '../ranking/userPosition/userPosition.component';
import { RankingFilterButtonComponent } from './rankingFilterButton/rankingFilterButton.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, AsyncPipe } from '@angular/common';
import { GpsOnlyService } from 'app/modules/browse/services/gps-only.service';
import { ApiHttpResponse, Top, Top3 } from '@kinomap-dev/ng-ui-lib';
import {
  Manufacturer,
  ManufacturerEquipment,
} from 'app/modules/browse/models/manufacturer.type';
import { EquipmentType } from 'app/modules/dashboard/models/equipment.model';

@Component({
  selector: 'app-watch-ranking-modal',
  templateUrl: './watchRankingModal.component.html',
  styleUrls: ['./watchRankingModal.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    TranslateModule,
    RankingFilterButtonComponent,
    GenderFilterBlockComponent,
    AgeFilterBlockComponent,
    EquipmentFilterBlockComponent,
    UserPositionComponent,
    InfiniteScrollDirective,
    PositionItemComponent,
    SkeletonModule,
    AsyncPipe,
    ChronoPipe,
  ],
})
export class WatchRankingModal implements OnInit, OnDestroy {
  @ViewChild(GenderFilterBlockComponent)
  genderFilterBlock: GenderFilterBlockComponent;
  @ViewChild(AgeFilterBlockComponent) ageFilterBlock: AgeFilterBlockComponent;
  @ViewChild(EquipmentFilterBlockComponent)
  equipmentFilterBlock: EquipmentFilterBlockComponent;
  @ViewChild('content') contentRef!: ElementRef;

  @Input() activityType: 'scenic' | 'gps-only' | 'mapmode';
  @Input() userLogged?: User = null;
  @Input() userRanked: any = null;
  @Input() videoId!: number;
  @Input() scrollContainerSelector: string;
  @Input() top3: Top3 = null;
  @Input() sport: string;

  machineType: string[] = [];
  ranking$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  rankingLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );
  brandsList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  filterOn: boolean = false;
  filterId: string = '';
  rankingParams: HttpParams = new HttpParams();
  emptyRanking: boolean = false;
  // API request prarams
  selectedAge: age = null;
  selectedGender: string = 'all';
  selectedManufacturer: Manufacturer = null;
  selectedEquipment: ManufacturerEquipment = null;
  selectedMachineType: EquipmentType = null;
  selectedFilters: string[] = [];

  // ranking api call variables
  page: number = 1;
  lastPage: number;
  subscriptions: Subscription[] = [];

  // RESPONSIVE VARIABLES
  xSmallScreen: boolean;

  constructor(
    private activityApi: ActivityApi,
    private responsive: BreakpointObserver,
    private router: Router,
    private equipmentApi: EquipmentApi,
    private gpsOnlyService: GpsOnlyService
  ) {}

  getRanking(): void {
    this.emptyRanking = false;

    switch (this.activityType) {
      case 'scenic':
        this.getScenicRanking(
          this.page,
          this.selectedAge,
          this.selectedGender,
          this.selectedMachineType,
          this.selectedManufacturer?.name
        );
        break;
      case 'gps-only':
      case 'mapmode': {
        this.getGpsOnlyRanking(
          this.page,
          this.selectedAge,
          this.selectedGender,
          this.selectedMachineType,
          this.selectedManufacturer?.name
        );
        break;
      }
      default:
        this.emptyRanking = true;
        throw new Error('Activity type not found');
    }
  }

  onScrollBottom(): void {
    if (this.page < this.lastPage) {
      this.rankingLoading$.next(true);
      this.page++;
      this.getRanking();
    }
  }

  navToActivity(activityId: number): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/browse/training-log', activityId]);
    });
  }

  navToUserProfil(userId: number): void {
    this.router.navigate(['dashboard', 'profile', userId]);
  }

  getAgeSection(age: number): string {
    let section: string = '';
    const ageSections: { ages: number[]; section: string }[] = [
      {
        ages: [0, 19],
        section: '-19',
      },
      {
        ages: [20, 34],
        section: '20-34',
      },
      {
        ages: [35, 44],
        section: '35-44',
      },
      {
        ages: [45, 54],
        section: '45-54',
      },
      {
        ages: [55, 64],
        section: '55-64',
      },
      {
        ages: [65, 999],
        section: '65+',
      },
    ];

    if (age === 0) {
      section = '--';
    } else {
      ageSections.forEach((ageSection: { ages: number[]; section: string }) => {
        if (age >= ageSection.ages[0] && age <= ageSection.ages[1])
          section = ageSection.section;
      });
    }
    return section;
  }

  setRankingParams(id: string): void {
    this.filterOn = false;
    if (this.filterId === 'age' || !id) {
      this.selectedAge = this.ageFilterBlock.getSelectedAge();
    }

    if (this.filterId === 'gender' || !id) {
      this.selectedGender = this.genderFilterBlock.genderSelected;
    }

    if (this.filterId === 'equipment' || !id) {
      this.getMachineType();
    }

    this.selectedFilters.push(this.filterId); // to display the dot to filter-btn.
    this.filterId = ''; // to turn off filter-btn select.
    this.page = 1;
    this.lastPage = undefined;
    this.ranking$.next([]);
    this.getRanking();
  }

  resetFilter(filter: string): void {
    this.activeFilter(filter);
    switch (filter) {
      case 'age':
        this.selectedAge = null;
        this.ageFilterBlock.selectedAgeIndex = null;
        this.selectedFilters = this.selectedFilters.filter(
          (el: string) => el !== 'age'
        );
        break;

      case 'gender':
        this.selectedGender = 'all';
        this.selectedFilters = this.selectedFilters.filter(
          (el: string) => el !== 'gender'
        );
        break;

      case 'equipment':
        this.selectedEquipment = null;
        this.selectedManufacturer = null;
        this.selectedMachineType = null;
        this.selectedFilters = this.selectedFilters.filter(
          (el: string) => el !== 'equipment'
        );
        break;

      default:
        this.selectedAge = null;
        this.selectedGender = 'all';
        this.selectedEquipment = null;
        this.selectedManufacturer = null;
        break;
    }

    this.lastPage = undefined;
    this.ranking$.next([]);
    this.getRanking();
  }

  activeFilter(filterName: string): void {
    if (this.filterId === filterName) {
      this.filterOn = false;
      this.filterId = '';
    } else {
      this.filterId = filterName;
      this.filterOn = true;
    }

    setTimeout((): void => {
      switch (filterName) {
        case 'gender':
          if (this.filterId === 'gender') {
            this.genderFilterBlock.show();
            this.ageFilterBlock.hide();
            this.equipmentFilterBlock.hide();
          }
          break;

        case 'age':
          if (this.filterId === 'age') {
            this.ageFilterBlock.show();
            this.genderFilterBlock.hide();
            this.equipmentFilterBlock.hide();
          }
          break;

        case 'equipment':
          if (this.filterId === 'equipment') {
            this.equipmentFilterBlock.show();
            this.ageFilterBlock.hide();
            this.genderFilterBlock.hide();
          }
          break;

        case null:
          if (this.contentRef) this.contentRef.nativeElement.style.gap = 32;
          this.genderFilterBlock.show();
          this.ageFilterBlock.show();
          this.equipmentFilterBlock.show();
          break;
      }
    }, 1);
  }

  getBrandsList(machineType: EquipmentType): void {
    if (
      this.equipmentFilterBlock &&
      !this.equipmentFilterBlock.brandsListLoading$.getValue()
    ) {
      this.equipmentFilterBlock.brandsListLoading$.next(true);
    }

    this.subscriptions.push(
      this.equipmentApi
        .getEquipments(machineType)
        .subscribe((res: Manufacturer[]) => {
          this.brandsList$.next(res);
          if (!res.includes(this.selectedManufacturer)) {
            this.selectedManufacturer = null;
          }
          if (this.equipmentFilterBlock) {
            this.equipmentFilterBlock.brandsListLoading$.next(false);
          }
        })
    );
  }

  getScenicRanking(
    page: number,
    selectedAge: age,
    selectedGender: string,
    selectedMachineType: string,
    selectedManufacturer: string
  ): void {
    this.rankingLoading$.next(true);
    let ageMax: number = null;
    let ageMin: number = null;
    let equipment: string = null;
    let gender: string = null;

    if (selectedAge != null) {
      ageMin = selectedAge.min;
      ageMax = selectedAge.max;
    }

    if (selectedGender != null) {
      switch (selectedGender) {
        case 'man':
          gender = 'M';
          break;
        case 'woman':
          gender = 'F';
          break;
        default:
          gender = null;
      }
    }

    equipment = selectedManufacturer !== '' ? selectedManufacturer : null;

    this.subscriptions = [
      ...this.subscriptions,
      this.activityApi
        .getActivityRanking(
          this.videoId,
          page,
          ageMin,
          ageMax,
          gender,
          selectedMachineType,
          equipment
        )
        .subscribe({
          next: (res: any) => {
            if (res.data.length === 0) {
              this.emptyRanking = true;
            } else {
              if (this.ranking$.getValue().length <= 3) {
                this.ranking$.next(res.data);
              } else {
                const updatedRanking: Top[] = [
                  ...this.ranking$.getValue(),
                  ...res.data,
                ];
                this.ranking$.next(updatedRanking);
              }
              this.emptyRanking = false;
            }

            this.lastPage = res.meta.last_page;
            this.rankingLoading$.next(false);
          },
          error: () => {
            this.emptyRanking = true;
            this.rankingLoading$.next(false);
          },
        }),
    ];
  }

  getGpsOnlyRanking(
    page: number,
    selectedAge: age,
    selectedGender: string,
    selectedMachineType: string,
    selectedManufacturer: string
  ): void {
    this.rankingLoading$.next(true);
    let ageMax: number = null;
    let ageMin: number = null;
    let equipment: string = null;
    let gender: string = null;

    if (selectedAge !== null) {
      ageMin = selectedAge.min;
      ageMax = selectedAge.max;
    }

    if (selectedGender !== null) {
      switch (selectedGender) {
        case 'man':
          gender = 'M';
          break;
        case 'woman':
          gender = 'F';
          break;
        default:
          gender = null;
      }
    }

    equipment = selectedManufacturer !== '' ? selectedManufacturer : null;

    this.subscriptions.push(
      this.gpsOnlyService
        .getRanking(
          this.videoId,
          page,
          ageMin,
          ageMax,
          gender,
          selectedMachineType,
          equipment
        )
        .subscribe((res: ApiHttpResponse<Top[]>) => {
          if (res.data.length === 0) {
            this.emptyRanking = true;
          } else {
            if (this.ranking$.getValue().length <= 3) {
              this.ranking$.next(res.data);
            } else {
              const updatedRanking: Top[] = [
                ...this.ranking$.getValue(),
                ...res.data,
              ];
              this.ranking$.next(updatedRanking);
            }
            this.emptyRanking = false;
          }

          this.lastPage = res.meta.last_page;
          this.rankingLoading$.next(false);
        })
    );
  }

  getMachineType(): void {
    if (this.machineType.length === 0) {
      switch (this.sport) {
        case 'cycling':
          this.machineType = [
            EquipmentType.HOME_TRAINER,
            EquipmentType.EXERCISE_BIKE,
          ];
          break;

        case 'running':
          this.machineType = [
            EquipmentType.HOME_TRAINER,
            EquipmentType.TREADMILL,
          ];
          break;

        case 'rowing':
          this.machineType = [EquipmentType.ROWING_MACHINE];
          break;
      }
    }
  }

  setSelectedValues({
    manufacturer,
    machineType,
    equipment,
  }: {
    manufacturer: Manufacturer;
    machineType: EquipmentType;
    equipment: ManufacturerEquipment;
  }): void {
    if (this.selectedMachineType !== machineType) {
      this.getBrandsList(machineType);
    }
    this.selectedMachineType = machineType;
    this.selectedManufacturer = manufacturer;
    this.selectedEquipment = equipment;
  }

  ngOnInit(): void {
    let orderlyRank: number[] = [];
    if (this.top3?.top?.length > 0)
      this.top3.top.forEach(({ rank, activity_id }: Top) => {
        if (activity_id != 0) {
          orderlyRank[rank - 1] = rank;
        }
      });

    this.ranking$.next(this.top3.top);

    this.getRanking();
    this.getMachineType();
    this.getBrandsList(this.selectedMachineType);

    this.subscriptions.push(
      this.responsive
        .observe(Breakpoints.XSmall)
        .subscribe((result: BreakpointState) => {
          this.xSmallScreen = result.matches ? true : false;
        })
    );
  }

  ngOnDestroy(): void {
    this.resetFilter('all');
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
